import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
import {
  SIGNUP_AGENT_ONBOARDING_ANSWERS_KEY,
  SIGNUP_AGENT_ONBOARDING_COMPLETED_PREFIX,
  SIGNUP_AGENT_ONBOARDING_PENDING_KEY,
} from 'constants/OnboardingConstant';
import { getUserIdFromToken, getUserEmailFromToken, isFirstTimeUserFromToken } from 'utils/jwtUtils';
import { safeGetToken } from 'utils/safeStorage';

export const getSignupAgentOnboardingUserKey = () => {
  const token = safeGetToken(SEC_AUTH_M_TK);
  const userId = getUserIdFromToken(token);
  const email = getUserEmailFromToken(token) || localStorage.getItem('_un');
  return userId || email || 'anonymous';
};

export const getSignupAgentOnboardingCompletedKey = () =>
  `${SIGNUP_AGENT_ONBOARDING_COMPLETED_PREFIX}${getSignupAgentOnboardingUserKey()}`;

export const hasCompletedSignupAgentOnboarding = () => {
  try {
    return localStorage.getItem(getSignupAgentOnboardingCompletedKey()) === 'true';
  } catch {
    return false;
  }
};

/** Mark this browser session/user as a new signup that should see the agent questionnaire. */
export const setSignupAgentOnboardingPending = () => {
  try {
    localStorage.setItem(SIGNUP_AGENT_ONBOARDING_PENDING_KEY, 'true');
    // Allow re-test / re-signup: clear prior completion for this user.
    localStorage.removeItem(getSignupAgentOnboardingCompletedKey());
  } catch {
    // no-op
  }
};

export const clearSignupAgentOnboardingPending = () => {
  try {
    localStorage.removeItem(SIGNUP_AGENT_ONBOARDING_PENDING_KEY);
  } catch {
    // no-op
  }
};

export const isSignupAgentOnboardingPending = () => {
  try {
    return localStorage.getItem(SIGNUP_AGENT_ONBOARDING_PENDING_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Show agent questionnaire only when JWT is_first_time_user is true
 * and this user has not completed/skipped it yet.
 * If the flag is false, /onboarding must not be accessible.
 */
export const shouldShowSignupAgentOnboarding = () => {
  const token = safeGetToken(SEC_AUTH_M_TK);
  if (!isFirstTimeUserFromToken(token)) return false;
  return !hasCompletedSignupAgentOnboarding();
};

export const markSignupAgentOnboardingCompleted = () => {
  try {
    localStorage.setItem(getSignupAgentOnboardingCompletedKey(), 'true');
    localStorage.removeItem(SIGNUP_AGENT_ONBOARDING_PENDING_KEY);
  } catch {
    // no-op
  }
};

export const saveSignupAgentOnboardingAnswers = (answers) => {
  try {
    localStorage.setItem(SIGNUP_AGENT_ONBOARDING_ANSWERS_KEY, JSON.stringify(answers || {}));
  } catch {
    // no-op
  }
};

export const getSignupAgentOnboardingAnswers = () => {
  try {
    const raw = localStorage.getItem(SIGNUP_AGENT_ONBOARDING_ANSWERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/** Read and clear prefill payload after agent/chat create form applies it. */
export const consumeSignupAgentOnboardingAnswers = () => {
  const answers = getSignupAgentOnboardingAnswers();
  try {
    localStorage.removeItem(SIGNUP_AGENT_ONBOARDING_ANSWERS_KEY);
  } catch {
    // no-op
  }
  return answers;
};

export const clearSignupAgentOnboardingStorage = () => {
  try {
    localStorage.removeItem(SIGNUP_AGENT_ONBOARDING_PENDING_KEY);
    localStorage.removeItem(SIGNUP_AGENT_ONBOARDING_ANSWERS_KEY);
  } catch {
    // no-op
  }
};

export const buildSuggestedAgentName = (answers = {}) => {
  const industry = answers.business_vertical || 'AI';
  const usecase = Array.isArray(answers.usecase_categories) && answers.usecase_categories.length
    ? answers.usecase_categories[0]
    : 'Assistant';
  const typeLabel =
    answers.agent_type === 'chat'
      ? 'Chat'
      : answers.agent_type === 'outbound'
        ? 'Outbound'
        : 'Inbound';
  return `${industry} ${usecase} ${typeLabel} Agent`.slice(0, 80);
};

const PROMPT_ASSIST_TO_VOICE_AGENT_LANGUAGE = {
  English: 'english',
  'Bahasa Indonesia': 'indonesian',
  Malaysia: 'malay',
  Thai: 'thai',
  Japanese: 'japanese',
  Mandarin: 'mandarin',
  Lao: 'lao',
  Tagalog: 'tagalog',
  Burmese: 'burmese',
  Hindi: 'hindi',
  Marathi: 'marathi',
  Tamil: 'tamil',
  Telugu: 'telugu',
  Bengali: 'bengali',
};

export const mapPromptAssistLanguageToVoiceAgent = (language) => {
  if (!language) return 'english';
  if (PROMPT_ASSIST_TO_VOICE_AGENT_LANGUAGE[language]) {
    return PROMPT_ASSIST_TO_VOICE_AGENT_LANGUAGE[language];
  }
  return String(language).trim().toLowerCase() || 'english';
};
